/**
 * Notifications — template editor + publish workflow.
 * Per docs/specs/notifications.md §5.2, §5.4, §8.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-25-v1
 */
import { AlertTriangle, Eye, Send } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { handleAdminApiAuthError, getAccessToken, useAdminSession } from '../../lib/auth';
import {
    buildTemplateTestEmailPayload,
    canPreviewTemplateDraft,
    renderTemplatePreview
} from '../../lib/email-template-preview';
import { appendAuditEntry } from '../../lib/mutations';
import { apiGet, apiPost, getHttpErrorMessage } from '../../lib/http';
import type {
    BroadcastRow,
    TemplateDetail,
    TemplatePerformance,
    TemplateVersionPayload
} from '../../lib/types';

interface TemplateTestEmailResponse {
    success: boolean;
    to: string;
    provider?: string | null;
    messageId?: string | null;
}

const TEMPLATE_VARIABLES = [
    'user_name',
    'user_email',
    'user_plan',
    'unsubscribe_url',
    'support_email',
    'dashboard_url'
];

function uniqueSorted(values: Array<string | undefined>): string[] {
    const seen = new Set<string>();
    for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
            seen.add(value);
        }
    }
    return [...seen].sort((left, right) => left.localeCompare(right));
}

function displayVersion(version: TemplateVersionPayload) {
    const tone =
        version.status === 'published'
            ? 'success'
            : version.status === 'draft'
              ? 'warning'
              : 'muted';
    return {
        tone,
        label: `v${version.version_number} · ${version.status}`
    };
}

function formatVersionDate(version?: TemplateVersionPayload): string {
    if (!version?.created_at) return '—';
    return new Date(version.created_at).toISOString().slice(0, 10);
}

function makePublishConfirmSlug(slug: string) {
    return `PUBLISH ${slug}`;
}

async function adminPut<T>(path: string, payload: unknown): Promise<T> {
    const headers = new Headers({
        accept: 'application/json',
        'content-type': 'application/json'
    });

    const token = await getAccessToken();
    if (token) {
        headers.set('authorization', `Bearer ${token}`);
    }

    const response = await fetch(path, {
        method: 'PUT',
        headers,
        credentials: 'omit',
        body: JSON.stringify(payload)
    });

    const text = await response.text();
    let body: unknown = null;
    try {
        body = text ? JSON.parse(text) : null;
    } catch {
        body = text;
    }

    if (!response.ok) {
        const detail =
            typeof body === 'object' &&
            body !== null &&
            'error' in body &&
            typeof body.error === 'string'
                ? body.error
                : undefined;

        if (
            path.startsWith('/api/admin/') &&
            (response.status === 401 || response.status === 403)
        ) {
            handleAdminApiAuthError(response.status as 401 | 403 | 404, detail);
        }

        throw new Error(detail || `Template update failed (${response.status})`);
    }

    return body as T;
}

export function TemplateEditorModal({
    template,
    onClose
}: {
    template: TemplatePerformance | null;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();
    const [detail, setDetail] = useState<TemplateDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedVersionId, setSelectedVersionId] = useState('');
    const [publishConfirm, setPublishConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [subject, setSubject] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [textContent, setTextContent] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const latestVersion = detail?.published_version || detail?.versions[0] || null;
    const activeVersion = detail?.versions.find((v) => v.id === selectedVersionId) || latestVersion;

    const allVariables = useMemo(() => {
        return uniqueSorted([
            ...TEMPLATE_VARIABLES,
            ...(detail?.supported_placeholders || []),
            ...(detail?.specific_placeholders || []),
            ...(activeVersion?.placeholder_analysis?.detected_placeholders || [])
        ]);
    }, [detail, activeVersion]);

    const preview = useMemo(
        () => renderTemplatePreview({ subject, htmlContent, textContent }, allVariables, session),
        [allVariables, htmlContent, session, subject, textContent]
    );

    const publishConfirmText = template ? makePublishConfirmSlug(template.slug) : '';
    const activeVersionIsSeed = activeVersion?.is_seed === true;
    const canPublish = Boolean(
        detail &&
        activeVersion &&
        selectedVersionId &&
        !activeVersionIsSeed &&
        !publishing &&
        publishConfirm.trim() === publishConfirmText
    );
    const canSave = Boolean(detail && subject.trim() && htmlContent.trim() && !saving);
    const canPreview = Boolean(
        detail &&
        canPreviewTemplateDraft({
            subject,
            htmlContent,
            textContent
        })
    );
    const canSendTest = Boolean(template && detail && canPreview && !sendingTest);
    const readiness = activeVersion?.placeholder_analysis;
    const readyForDirectSend = readiness?.ready_for_direct_send;

    useEffect(() => {
        if (!template) {
            setDetail(null);
            setError('');
            setLoading(false);
            setSelectedVersionId('');
            setPublishConfirm('');
            setSubject('');
            setHtmlContent('');
            setTextContent('');
            setSendingTest(false);
            setPreviewOpen(false);
            return;
        }

        let cancelled = false;
        setPreviewOpen(false);
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const fetched = await apiGet<TemplateDetail>(
                    `/api/admin/notifications/templates/${template.slug}`
                );

                if (cancelled) return;

                setDetail(fetched);
                setSelectedVersionId(
                    fetched.published_version?.id || fetched.versions[0]?.id || ''
                );
                setPublishConfirm('');

                const editableBase = fetched.published_version || fetched.versions[0];
                setSubject(editableBase?.subject || `[PD] ${template.slug.replace(/_/g, ' ')}`);
                setHtmlContent(editableBase?.html_content || '');
                setTextContent(editableBase?.text_content || '');
            } catch (error_) {
                if (!cancelled) {
                    setError(error_ instanceof Error ? error_.message : 'Template load failed');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [template]);

    const handleSaveDraft = async () => {
        if (!template || !detail) return;

        setSaving(true);
        setError('');
        try {
            const updated = await adminPut<TemplateDetail>(
                `/api/admin/notifications/templates/${template.slug}`,
                {
                    subject: subject.trim(),
                    html_content: htmlContent,
                    text_content: textContent,
                    from_email: detail.from_email,
                    from_name: detail.from_name,
                    reply_to_email: detail.reply_to_email,
                    reply_to_name: detail.reply_to_name,
                    supported_placeholders: detail.supported_placeholders,
                    status: 'draft'
                }
            );

            setDetail(updated);
            setSelectedVersionId(
                updated.published_version?.id || updated.versions[0]?.id || selectedVersionId
            );
            await qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] });
            toast('Template draft saved', {
                detail: template.slug,
                kind: 'success'
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Save failed';
            toast('Template draft save failed', { kind: 'danger', detail: message });
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!template || !activeVersion || !canPublish) return;

        setPublishing(true);
        setError('');
        try {
            const published = await apiPost<TemplateDetail>(
                `/api/admin/notifications/templates/${template.slug}/publish`,
                {
                    version_id: activeVersion.id
                }
            );

            setDetail(published);
            setSelectedVersionId(published.published_version?.id || selectedVersionId);
            setPublishConfirm('');

            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'notifications.template.publish',
                target_type: 'notification_template',
                target_id: template.slug,
                severity: 'sensitive',
                reason: `Version ${activeVersion.version_number} publish from admin workflow`
            });

            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'kpis'] })
            ]);

            toast('Template published', {
                detail: `${template.slug} · v${activeVersion.version_number}`,
                kind: 'info'
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Publish failed';
            toast('Template publish failed', { kind: 'danger', detail: message });
            setError(message);
        } finally {
            setPublishing(false);
        }
    };

    const handleSendTestEmail = async () => {
        if (!template || !detail || !canSendTest) return;

        setSendingTest(true);
        setError('');
        try {
            const result = await apiPost<TemplateTestEmailResponse>(
                `/api/admin/notifications/templates/${template.slug}/test-send`,
                buildTemplateTestEmailPayload({
                    detail,
                    selectedVersionId,
                    subject: subject.trim(),
                    htmlContent,
                    textContent
                })
            );

            toast('Template test email sent', {
                kind: 'success',
                detail: `${result.to}${result.provider ? ` · ${result.provider}` : ''}`
            });
        } catch (err) {
            const message = getHttpErrorMessage(err, 'Template test email failed');
            toast('Template test email failed', { kind: 'danger', detail: message });
            setError(message);
        } finally {
            setSendingTest(false);
        }
    };

    if (!template) return null;

    return (
        <>
            <Modal
                open={!!template && !previewOpen}
                onClose={() => onClose()}
                title={`Edit template · ${template.slug}`}
                subtitle={`${template.category} · ${template.sent_30d.toLocaleString()} sent 30d`}
                width={760}
                footer={
                    <>
                        <button className="btn btn-ghost" onClick={onClose}>
                            Close
                        </button>
                        <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            disabled={!canPreview || loading}
                        >
                            <Eye size={14} /> Preview
                        </button>
                        <RoleButton
                            scope="support_ok"
                            className="btn btn-ghost"
                            onClick={handleSaveDraft}
                            disabled={!canSave || loading || saving}
                        >
                            {saving ? 'Saving…' : 'Save draft'}
                        </RoleButton>
                        <RoleButton
                            scope="owner_only"
                            className="btn btn-primary"
                            onClick={handlePublish}
                            disabled={!canPublish || loading}
                        >
                            {publishing ? 'Publishing…' : 'Publish selected version'}
                        </RoleButton>
                    </>
                }
            >
                {loading ? (
                    <div className="empty-state">
                        <div className="title">Loading template details…</div>
                    </div>
                ) : detail ? (
                    <>
                        {error && <TemplateModalError error={error} />}
                        <div
                            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}
                        >
                            <Chip tone="muted">{detail.category}</Chip>
                            <Chip tone={readyForDirectSend ? 'success' : 'warning'}>
                                {readyForDirectSend === undefined
                                    ? 'Direct send status unknown'
                                    : readyForDirectSend
                                      ? 'Direct send ready'
                                      : 'Direct-send review needed'}
                            </Chip>
                            <Chip tone="muted">
                                storage: {detail.storage_state.replace('_', ' ')}
                            </Chip>
                            <Chip tone="muted">provider: {detail.provider}</Chip>
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="template-version">
                                Version to publish
                            </label>
                            <select
                                id="template-version"
                                className="form-input"
                                value={selectedVersionId}
                                disabled={!detail.versions.length}
                                onChange={(e) => setSelectedVersionId(e.target.value)}
                            >
                                {!detail.versions.length && (
                                    <option value="">No versions available</option>
                                )}
                                {(detail.versions || []).map((version) => {
                                    const label = displayVersion(version);
                                    return (
                                        <option key={version.id} value={version.id}>
                                            {`${label.label} (${formatVersionDate(version)})`}
                                        </option>
                                    );
                                })}
                            </select>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                                {activeVersionIsSeed
                                    ? 'This is the bundled legacy export seed. Migrate it into managed storage or save a draft before publishing.'
                                    : 'Select the version you want to publish. Publish writes an audit row and marks it as current sent copy.'}
                            </div>
                        </div>

                        {detail.description ? (
                            <div
                                style={{
                                    border: '1px solid var(--border)',
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 12,
                                    background: 'var(--bg-input)'
                                }}
                            >
                                <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                                    {detail.description}
                                </div>
                            </div>
                        ) : null}

                        {readiness && !readiness.ready_for_direct_send && (
                            <div
                                style={{
                                    border: '1px solid var(--warning-border)',
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 12,
                                    background: 'var(--warning-surface)'
                                }}
                            >
                                <div className="form-label" style={{ marginBottom: 8 }}>
                                    Readiness checks
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                                    {readiness.unsupported_helpers?.length ? (
                                        <li>
                                            Unsupported helper usage:{' '}
                                            {readiness.unsupported_helpers.join(', ')}
                                        </li>
                                    ) : null}
                                    {readiness.missing_from_contract?.length ? (
                                        <li>
                                            Missing placeholders:{' '}
                                            {readiness.missing_from_contract.join(', ')}
                                        </li>
                                    ) : null}
                                    {readiness.unsupported_helpers?.length ||
                                    readiness.missing_from_contract?.length ? null : (
                                        <li>No blocklisting reason surfaced in payload.</li>
                                    )}
                                </ul>
                            </div>
                        )}

                        <div className="form-field">
                            <label className="form-label" htmlFor="template-subject">
                                Subject line
                            </label>
                            <input
                                id="template-subject"
                                className="form-input"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </div>

                        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
                            <div className="form-field" style={{ marginBottom: 0 }}>
                                <label className="form-label" htmlFor="template-from">
                                    From name
                                </label>
                                <input
                                    id="template-from"
                                    className="form-input"
                                    value={detail.from_name}
                                    onChange={(e) => {
                                        setDetail((current) =>
                                            current
                                                ? { ...current, from_name: e.target.value }
                                                : current
                                        );
                                    }}
                                />
                            </div>
                            <div className="form-field" style={{ marginBottom: 0 }}>
                                <label className="form-label" htmlFor="template-from-email">
                                    From email
                                </label>
                                <input
                                    id="template-from-email"
                                    className="form-input"
                                    value={detail.from_email}
                                    onChange={(e) => {
                                        setDetail((current) =>
                                            current
                                                ? { ...current, from_email: e.target.value }
                                                : current
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="template-html">
                                HTML content
                            </label>
                            <textarea
                                id="template-html"
                                className="form-textarea"
                                style={{ minHeight: 190 }}
                                value={htmlContent}
                                onChange={(e) => setHtmlContent(e.target.value)}
                            />
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="template-text">
                                Text content
                            </label>
                            <textarea
                                id="template-text"
                                className="form-textarea"
                                style={{ minHeight: 140 }}
                                value={textContent}
                                onChange={(e) => setTextContent(e.target.value)}
                            />
                            <div className="form-hint">
                                Text and HTML are validated with the template placeholder analyzer
                                before each draft save.
                            </div>
                        </div>

                        <div className="form-field">
                            <span className="form-label">Available variables</span>
                            <div className="template-variable-grid">
                                {allVariables.map((v) => (
                                    <div key={v} className="template-variable-row">
                                        <code>{`{{${v}}}`}</code>
                                        <span>{preview.placeholderData[v] || '-'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="publish-confirm">
                                Confirm publish
                            </label>
                            <input
                                id="publish-confirm"
                                className="form-input"
                                value={publishConfirm}
                                onChange={(e) => setPublishConfirm(e.target.value)}
                                placeholder={`Type "${publishConfirmText}" to enable publish`}
                            />
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                Confirmation is required before any publish action. This writes an
                                immutable admin audit record.
                            </div>
                        </div>
                    </>
                ) : error ? (
                    <TemplateModalError error={error} />
                ) : null}
            </Modal>

            <Modal
                open={!!template && previewOpen}
                onClose={() => setPreviewOpen(false)}
                title={`Preview template · ${template.slug}`}
                subtitle={`Test emails go to ${session.email}`}
                width={980}
                footer={
                    <>
                        <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={() => setPreviewOpen(false)}
                        >
                            Back to editor
                        </button>
                        <RoleButton
                            scope="support_ok"
                            className="btn btn-primary"
                            onClick={handleSendTestEmail}
                            disabled={!canSendTest || loading}
                        >
                            <Send size={14} /> {sendingTest ? 'Sending…' : 'Send test'}
                        </RoleButton>
                    </>
                }
            >
                {error && <TemplateModalError error={error} />}
                <div className="template-preview-grid">
                    <div className="template-preview-panel">
                        <div className="template-preview-subject">
                            {preview.subject || 'No subject'}
                        </div>
                        <iframe
                            className="template-preview-frame"
                            sandbox=""
                            title="Rendered HTML email preview"
                            srcDoc={
                                preview.html ||
                                '<!doctype html><html><body>No HTML content</body></html>'
                            }
                        />
                    </div>
                    <pre className="template-preview-text">{preview.text || 'No text content'}</pre>
                </div>

                <div className="form-field template-preview-values">
                    <span className="form-label">Placeholder values</span>
                    <div className="template-variable-grid">
                        {allVariables.map((v) => (
                            <div key={v} className="template-variable-row">
                                <code>{`{{${v}}}`}</code>
                                <span>{preview.placeholderData[v] || '-'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    );
}

function TemplateModalError({ error }: { error: string }) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                gap: 10,
                padding: 12,
                background: 'var(--danger-surface)',
                border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)',
                borderRadius: 8,
                marginBottom: 12,
                color: 'var(--text-primary)',
                fontSize: 12
            }}
        >
            <AlertTriangle size={16} style={{ color: 'var(--danger)', marginTop: 1 }} />
            <div>
                <strong>Template action failed.</strong>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {error}
                </div>
            </div>
        </div>
    );
}

export function BroadcastModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();
    const [title, setTitle] = useState('');
    const [audience, setAudience] = useState<'all' | 'paying' | 'active_30d' | 'custom'>(
        'active_30d'
    );
    const [template, setTemplate] = useState('release_notes');
    const [scheduledAt, setScheduledAt] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setTitle('');
        setAudience('active_30d');
        setTemplate('release_notes');
        setScheduledAt('');
    };

    async function submitBroadcast() {
        if (!title.trim() || submitting) return;
        setSubmitting(true);
        try {
            const created = await apiPost<BroadcastRow>('/api/admin/notifications/broadcasts', {
                title: title.trim(),
                audience,
                template,
                scheduled_at: scheduledAt || undefined
            });
            await qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'broadcasts'] });
            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'notification.broadcast_created',
                target_type: 'broadcast',
                target_id: created.id,
                severity: 'routine',
                reason: title.trim()
            });
            toast('Broadcast queued', {
                detail: `${created.audience} · ${created.count.toLocaleString()} recipients`
            });
            reset();
            onClose();
        } catch (error) {
            toast('Broadcast failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Broadcast was not queued')
            });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!submitting) onClose();
            }}
            title="New broadcast"
            subtitle="Rate-limited to 100 emails/sec"
            width={560}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>
                        Close
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        disabled={!title.trim() || submitting}
                        onClick={submitBroadcast}
                    >
                        {submitting ? 'Queueing…' : 'Queue broadcast'}
                    </RoleButton>
                </>
            }
        >
            <div className="form-field">
                <label className="form-label" htmlFor="bc-title">
                    Title
                </label>
                <input
                    id="bc-title"
                    className="form-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="v2.5.1 patch release"
                />
            </div>
            <div className="form-field">
                <label className="form-label" htmlFor="audience">
                    Audience
                </label>
                <select
                    id="audience"
                    className="form-input"
                    value={audience}
                    onChange={(e) =>
                        setAudience(e.target.value as 'all' | 'paying' | 'active_30d' | 'custom')
                    }
                >
                    <option value="all">All users (~12,847)</option>
                    <option value="paying">Paying users (~812)</option>
                    <option value="active_30d">Active 30d (~7,120)</option>
                    <option value="custom">Custom segment SQL</option>
                </select>
                <div className="form-hint">
                    Honours <code>email_opt_out</code> and per-category preferences.
                </div>
            </div>
            <div className="form-field">
                <label className="form-label" htmlFor="template">
                    Template
                </label>
                <select
                    id="template"
                    className="form-input"
                    value={template}
                    onChange={(e) => setTemplate(e.target.value)}
                >
                    <option value="release_notes">release_notes</option>
                    <option value="legal_update">legal_update</option>
                    <option value="feedback_request">feedback_request</option>
                </select>
            </div>
            <div className="form-field">
                <label className="form-label" htmlFor="schedule">
                    Schedule (optional)
                </label>
                <input
                    id="schedule"
                    type="datetime-local"
                    className="form-input"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                />
                <div className="form-hint">Leave blank to send immediately.</div>
            </div>

            <div className="form-hint">
                Broadcasts are queued server-side first; delivery workers can consume this queue
                when provider-specific send automation is enabled.
            </div>
        </Modal>
    );
}
